/**
 * ANA-R1-13 — the pure "should we prompt?" decision.
 *
 * Locked decision (OQ-A1-4 a): prompt on EVERY open of a not-fully-cached data
 * set, unless the user ticked "don't ask again for this data set". Never
 * auto-download. The prompt must not delay first paint, so we also require the
 * surface to be interactive (the PROG-01 readiness gate has reported ready).
 *
 * Kept as a pure function so the prompt-decision logic is unit-tested in
 * isolation from React, the dialog, and the cache machinery.
 */

export interface DownloadPromptInputs {
  /**
   * True once the data set's images are all in the cache (cachedCount >=
   * totalCount, totalCount > 0). A fully-cached data set never prompts — there
   * is nothing left to download.
   */
  fullyCached: boolean;
  /** True when the user ticked "don't ask again for this data set". */
  suppressed: boolean;
  /**
   * True once the analysis surface is interactive (PROG-01 `isReady`). We only
   * mount the prompt after this so it never delays first paint (AC3).
   */
  surfaceReady: boolean;
  /**
   * True when the total image count is known (> 0). With an unknown / empty
   * data set there is nothing to offer to download, so we never prompt.
   */
  hasImages: boolean;
}

/**
 * Decide whether to show the dataset-open download prompt.
 *
 * Prompt iff: the surface is interactive AND the data set has images AND it is
 * not already fully cached AND the user has not suppressed it for this data set.
 */
export function shouldPromptDownload(inputs: DownloadPromptInputs): boolean {
  if (!inputs.surfaceReady) return false;
  if (!inputs.hasImages) return false;
  if (inputs.fullyCached) return false;
  if (inputs.suppressed) return false;
  return true;
}
